import { AUTH_PREFIX_PATH, REDIRECT_URL_KEY, UNAUTHENTICATED_ENTRY, EXPIRED_PASSWORD_ENTRY } from "configs/AppConfig";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "auth/SupabaseClient";
import { signOut } from "store/slices/authSlice";

const ProtectedRoute = () => {
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const lockedValue = localStorage.getItem("LOCKSCREEN");
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const hasCheckedStatus = useRef(false);
  
  const getSupabaseAuthTokenKey = () => {
    const supabaseUrl = new URL(process.env.REACT_APP_SUPABASE_URL);
    const supabaseHostKey = supabaseUrl.host.split(".")[0];
    return `sb-${supabaseHostKey}-auth-token`;
  };
  
  const supabaseAuthTokenKey = getSupabaseAuthTokenKey();

  let supabaseAuth = JSON.parse(localStorage.getItem(supabaseAuthTokenKey)) || null;

  const getDateNDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  };

  const date60DaysAgo = getDateNDaysAgo(60);
  const isPasswordExpired = supabaseAuth ? new Date(supabaseAuth?.user.user_metadata.password_last_updated_at) < date60DaysAgo : false;

  // Check user status when accessing protected routes
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!token || isCheckingStatus || hasCheckedStatus.current) return;

      try {
        setIsCheckingStatus(true);
        hasCheckedStatus.current = true;
        const userId = localStorage.getItem("USER_ID");
        if (!userId) {
          setIsCheckingStatus(false);
          hasCheckedStatus.current = false;
          return;
        }

        // Check user status from Users table
        const { data: userData, error: userError } = await supabase
          .from('Users')
          .select('status')
          .eq('id', userId)
          .single();

        if (userError) {
          console.error('Error fetching user status in ProtectedRoute:', userError);
          setIsCheckingStatus(false);
          // If we can't verify status, sign out for security
          await supabase.auth.signOut();
          dispatch(signOut());
          localStorage.removeItem("USER_ID");
          localStorage.removeItem("AUTH_TOKEN");
          localStorage.removeItem("USER_DETAILS");
          localStorage.removeItem("MAP_DETAILS");
          localStorage.removeItem("ORG_DETAILS");
          localStorage.removeItem("LOCKSCREEN");
          localStorage.removeItem("CURRENT_LOCATION");
          window.location.href = `${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`;
          return;
        }

        // Check if user status is "InActive"
        if (userData && userData.status === 'InActive') {
          setIsCheckingStatus(false);
          // Sign out the user
          await supabase.auth.signOut();
          dispatch(signOut());
          // Clear all localStorage
          localStorage.removeItem("USER_ID");
          localStorage.removeItem("AUTH_TOKEN");
          localStorage.removeItem("USER_DETAILS");
          localStorage.removeItem("MAP_DETAILS");
          localStorage.removeItem("ORG_DETAILS");
          localStorage.removeItem("LOCKSCREEN");
          localStorage.removeItem("CURRENT_LOCATION");
          // Redirect to login
          window.location.href = `${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`;
        } else {
          setIsCheckingStatus(false);
        }
      } catch (error) {
        console.error('Error checking user status in ProtectedRoute:', error);
        setIsCheckingStatus(false);
        // On error, sign out for security
        await supabase.auth.signOut();
        dispatch(signOut());
        localStorage.removeItem("USER_ID");
        localStorage.removeItem("AUTH_TOKEN");
        localStorage.removeItem("USER_DETAILS");
        localStorage.removeItem("MAP_DETAILS");
        localStorage.removeItem("ORG_DETAILS");
        localStorage.removeItem("LOCKSCREEN");
        localStorage.removeItem("CURRENT_LOCATION");
        window.location.href = `${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`;
      }
    };

    if (token && !hasCheckedStatus.current) {
      checkUserStatus();
    }

    // Reset check flag when token changes
    return () => {
      if (!token) {
        hasCheckedStatus.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch]);

  if (!token) {
    return (
      <Navigate to={`${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}?${REDIRECT_URL_KEY}=${location.pathname}`} replace />
    );
  } else if (lockedValue) {
    return <Navigate to={`${AUTH_PREFIX_PATH}/lock-screen?${REDIRECT_URL_KEY}=${location.pathname}`} replace />;
  } else if (isPasswordExpired && location.pathname !== EXPIRED_PASSWORD_ENTRY) {
    return <Navigate to={`${EXPIRED_PASSWORD_ENTRY}?${REDIRECT_URL_KEY}=${location.pathname}`} replace />;
  } else if (isCheckingStatus) {
    // Show loading while checking status
    return null;
  }
  return <Outlet />;
};

export default ProtectedRoute;